import Request from 'superagent';
import moment from 'moment';
import noCache from 'superagent-no-cache';
import {AdminEndpoints, FarmersEndpoints, FBOEndpoints, TractorOwnerEndpoints, UsersEndpoints} from "./ProxyEndpoints";
import AppGlobals from "./AppGlobals";
import {notifyUserInfoChanged} from "./Events/EventSource";
import {UserRoles} from "./Constants";
import Utils from "./Utils";
import NotificationHandler from "./NotificationHandler";
import QueryString from 'query-string';

/**
 * Prepares an response error object
 * @constructor
 */
const PrepareError = (err,response) => {

    //  Determines whether the error is a network error
    err.isConnectionError = () => {
        return !err.hasOwnProperty('status') || !err.hasOwnProperty('response');
    };

    //  Determine whether request was sent but got error response
    err.isServerError = () => {
        return err.hasOwnProperty('status');
    };

    //  Returns the error of message in the response
    err.getServerError = () => {

        //  check for error response
        if(err.isServerError() && response.body && response.body.status !== 0){
            return response.body.error.message;
        }

        return null;
    };
};


/**
 * Generate order state
 * @param orderState
 */
export function generateOrderString(orderState) {

}

/**
 * Generate filter state
 */
export function generateFilterString(filters) {

    let filter = undefined;

    /**
     * Applies filter
     * @param obj The filter object
     * @param custom Indicates the object is for custom filtering. Thus, has custom operator...
     */
    const applyFilter = (obj , custom) => {

        if(!filter){
            filter = [];
        }

        if(custom){
            filter.push({ field: obj.key , value: obj.value , operator: obj.operator || "="  })
        }
        else{

            Object.keys(obj).forEach((k) => {
                filter.push({ field : k , value: obj[k] , operator: '=' });
            });

        }
    };

    if(Array.isArray(filters)){

        for(let i = 0 ; i < filters.length ; i++)
            applyFilter(filters[i],true);

    }
    else if(typeof filters === "object"){
        applyFilter(filters);
    }
    else if(typeof filters === "string"){
        filter = filters;
    }

    if(filter){

        if(Array.isArray(filter))
            return filter.map((f) => { return `${f.field}${f.operator}${encodeURIComponent(f.value)}`; }).join(",");

        return filter;
    }

}

/**
 * Prepares a response object
 * @param response
 * @constructor
 */
const PrepareResponse = (response) => {

    //  Get actual data
    response.getPayload = () => {
        return TTLProxy.getResponseData(response);
    };

    //  Get count
    response.getPayloadCount = () => {
        return TTLProxy.getResponseCount(response);
    };

    //  Get operation status message
    response.getOperationMessage = () => {
        return TTLProxy.getResponseMessage(response);
    };

    //  Get server response status
    response.getResponseServerStatus = () =>{
      return TTLProxy.getResponseServerStatus(response);
    };

};

/**
 * Navigates the user to login
 */
function navigateLogin() {
    window.location.href = "/login";
}

/**
 * Exposes an interface for communicating with TTL Core-API Backend
 */
export default class TTLProxy{

    //  The API-Key
    apiKey;

    //  The server address
    serverAddress;

    //  The browser history manipulator
    history;

    //  The last date sent by server
    lastDate;

    /**
     * Builds an absolute uri to be sent directly to the server
     * @param url
     * @returns {string}
     */
    getUrl(url){

        if(url[0] === '/')
            url = url.substr(1);

        return `${this.serverAddress}/${url}`;
    }

    constructor( apiKey , address ) {

        //  set api-key
        this.apiKey = apiKey;

        //  set server address
        this.serverAddress = address;
    }

    setAuthInfo(token , user , refreshToken  , timeIssued , expiresAt ){

        const authInfo = {
            token: token,
            user: user,
            refreshToken: refreshToken,
            timeIssued: timeIssued || moment().toDate(),
            expiresAt: expiresAt ||  moment().add( process.env.REACT_APP_MAX_JWT_DURATION_HOURS || 3,"hours").toDate()
        };

        //  save session info
        window.localStorage.setItem( 'auth_info', JSON.stringify(authInfo) );

        // overwrite reference
        this._authInfo = authInfo;
    }

    /**
     * Retrieves the authentication info of current session
     * @returns {*}
     */
    getAuthInfo(){

        //  check instance reference
        if(this._authInfo){

            if(!window.localStorage.getItem('auth_info')){

                //  Oops! The storage must not be the best
                //  persist setting
                window.localStorage.setItem('auth_info',JSON.stringify(this._authInfo));
            }

            return this._authInfo;
        }

        let authInfo = window.localStorage.getItem('auth_info');

        if(authInfo){

            const ticket = JSON.parse(authInfo);

            if(moment().isAfter( moment(ticket.timeIssued).add(process.env.REACT_APP_MAX_USER_SESSION_HOURS,"hours"))){
                window.localStorage.removeItem("auth_info");
                return null;
            }


            return this._authInfo = ticket;
        }

        return null;
    }


    /**
     * Retrieves the associated user info
     * @returns {*}
     */
    getUserInfo(){

        let authInfo = this.getAuthInfo();
        if(authInfo){
            return authInfo.user;
        }
    }

    /**
     * Updates the user info session variable
     * @param user
     */
    setUserInfo(user){

        let authInfo = this.getAuthInfo();
        if(authInfo){
            authInfo.user = user;
            window.localStorage.setItem( 'auth_info',JSON.stringify(authInfo) );

            //  broadcast user info changed event
            notifyUserInfoChanged(user);
        }

    }

    /**
     * Gets the user role
     * @returns {null}
     */
    getUserRole(){
        let user = this.getUserInfo();
        if(user)
            return user.role;
        return null;
    }

    /**
     * Indicates that the current user is
     * @returns {*}
     */
    isSuperUser(){

        const user = this.getUserInfo();
        if(user && user.role === UserRoles.Admin){
            return user.admin.super_user;
        }

        return false;
    }

    /**
     * Returns the last time captured by server
     * @returns {*}
     */
    getServerDate(){
       return this.lastDate;
    }

    /**
     * Checks whether the proxy is authenticated and ready for communication
     */
    isAuthenticated(){
        return !!this.getAuthInfo();
    }


    /**
     * Redirects if session is expired when response was got
     * @param response
     * @param doneCallback
     * @param errorCallback
     * @param reRunCallback
     */
    inspectResponse(response, doneCallback, errorCallback, reRunCallback){

        //  unauthorized response
        if(response && response.unauthorized && this.isAuthenticated()){

            //  try to see if session can be revived
            if(response.body && response.body.error && response.body.error.message.toLowerCase().indexOf("jwt expired") >= 0){

                return this.refreshToken().then(() => reRunCallback()).catch(err => {

                    if(err.response && err.response.forbidden){
                        return this.reset(true);
                    }

                    errorCallback(err);
                });

            }

        }

        doneCallback();
    }

    /**
     * Sets the history object
     * @param history
     */
    setHistory(history){
        this.history = history;
    }

    /**
     * Initialize proxy with credentials supplied
     * @param credentials
     */
    login(credentials){

        const me = this;
        return this.processRequest(UsersEndpoints.login(credentials)).then((response) =>{

            //  set authentication info
            me.handleAuthResponse(response);

            return response;

        });
    }

    /**
     * Processes authentication response
     * @param response
     * @returns {boolean}
     */
    handleAuthResponse(response){

        if(response.ok) {

            //  get body
            let authInfo = response.body;

            //  set authentication info
            this.setAuthInfo(authInfo.token, authInfo.user, authInfo.refreshToken);

            return true;
        }

        return false;

    }


    /**
     * Returns the singleton proxy instance.
     * Note: The proxy returned can be null if not setup
     * @returns {*}
     */
    static getInstance(){
        return AppGlobals.get('proxy');
    }

    /**
     * Returns the payload of server response
     * @param response
     * @returns {any}
     */
    static getResponseData(response){

        let body = response.body;
        if(body.status === 0){

            //  we're certain the response has valid data
            return response.body.payload.data;
        }

        return  null;
    }

    /**
     * Returns the total count of records from server response
     * @param response
     * @returns {null}
     */
    static getResponseCount(response){

        let body = response.body;
        if(body.status === 0){

            //  we're certain the response has valid data
            return response.body.payload.count;
        }

        return  null;
    }


    /**
     * Returns the server status from response. This is not to be mistaken for HTTP status
     * @param response
     */
    static getResponseServerStatus(response){
        return response.status;
    }


    /**
     * Refreshes session expired token
     * @param callback
     */
    refreshToken(callback){

        const authInfo = this.getAuthInfo();
        const me = this;
        return new Promise((resolve,reject) => {

            Request.post(this.getUrl(`auth/local/refresh`))
                .set('x-api-key',me.apiKey)
                .set('Authorization', `Bearer ${authInfo.token}`) //send along expired token
                .set('x-refresh-token' , authInfo.refreshToken )
                .end((err,response) => {

                    if(err){
                        return reject(err);
                    }

                    const body = response.body;
                    this.setAuthInfo(body.token, authInfo.user , body.refreshToken);

                    resolve(response);
                });

        });


    }

    /**
     * Pre Checks parameters before beginning execution of request
     */
    preExecuteRequest(){

        const me  = this;

        return new Promise((resolve,reject) => {

            if (me.needTokenRefresh()){

                //  refresh token
                return me.refreshToken().catch(err => {

                    if(err.response && err.response.forbidden){

                        //  sign out here
                        this.reset(true);
                    }

                    return reject(err);

                }).then(resolve);

            }
            else{
                resolve();
            }

        });

    }

    /**
     * Determines whether token refresh is required
     * @returns {boolean}
     */
    needTokenRefresh(){
        return this.isSessionExpired();
    }

    /**
     * Determines whether the session is expired
     * @returns {boolean}
     */
    isSessionExpired(){

        const authInfo = this.getAuthInfo();
        if(authInfo === null){
            return false;
        }

        return (moment().isBefore(authInfo.timeIssued)) || (moment().isAfter(authInfo.expiresAt));

    }

    reset(navigateAway = false){

        //
        this._authInfo = null;

        //
        window.onbeforeunload = null; //disable unload strategies
        window.sessionStorage.clear();  // wipe session
        window.localStorage.removeItem("auth_info");

        if(navigateAway) {
            navigateLogin();
        }

    }

    /**
     * Clears session info
     */
    signOut(){

        const me = this;
        if(this.isAuthenticated()) {

            return this.processRequest(UsersEndpoints.logout()).then((response) =>{
                this.reset();
                return response;
            });

        }

    }

    /**
     * Updates the associated entity of the user
     * @param payload
     */
    updateUserEntity(payload){

        const me = this;

        let endpoint;
        const user = this.getUserInfo();
        const role = user.role;
        const entity = Utils.getUserEntity(user);
        const entityId = entity.id;

        if(Utils.hasRole(role,UserRoles.Admin)) {
            endpoint = AdminEndpoints.update(entityId,payload);
        }
        else if(Utils.hasRole(role,UserRoles.FBO)){
            endpoint = FBOEndpoints.update(entityId,payload);
        }
        else if(Utils.hasRole(role,UserRoles.Owner)){
            endpoint = TractorOwnerEndpoints.updateOwner(entityId,payload);
        }
        else if(Utils.hasRole(role,UserRoles.Farmer)){
            endpoint = FarmersEndpoints.update(entityId,payload);
        }

        return this.processRequest(endpoint).then(response =>{

            if(response.ok){

                return this.processRequest(UsersEndpoints.getMyself()).then(res => {

                    const user = res.getPayload();

                    //  update session
                    this.setUserInfo(user);

                    //
                    return user;
                });


            }

        });
    }


    /**
     * Update user info
     */
    updateMyself(payload){

        let endpoint;
        const me = this;
        const user = this.getUserInfo();

        return this.processRequest(UsersEndpoints.updateUser(user.id,payload)).then(response =>{

            if(response.ok){

                //  update user
                for(let k in payload){

                    if(user[k] != payload[k] && payload[k] !== undefined) {
                        user[k] = payload[k];
                    }

                }

                //  update session
                me.setUserInfo(user);

                return user;
            }

        });
    }


    /**
     * Process request to query
     * @param endpoint
     * @param options
     */
    buildQueryString(endpoint,options){


        //  support for one arg call
        if(typeof endpoint == "object"){
            options = endpoint;
            endpoint = options.url;
        }

        return this._getQueryString(endpoint,options);
    }

    _getQueryString(endpoint,options){

        const me = this;
        let id;
        let method = "get";
        let qsArray = [];

        if(Array.isArray(id) && method === "get"){
            endpoint += '/many';
        }

        let _url  = endpoint + (id ? ( Array.isArray(id)  ? '' : ("/" + id) ) : '' ) ;
        let queryString = this.getUrl(_url);

        if(me.apiKey){
            qsArray.push({ "api_key": encodeURIComponent(me.apiKey) });
        }

        if(!options.noAuth) {
            let authInfo = me.getAuthInfo();
            if (authInfo) {
                qsArray.push({ "access_token" : encodeURIComponent(authInfo.token)  });
            }
        }

        //  set query parameters
        let query = options.query;
        if(query) {
            qsArray = qsArray.concat(query);
        }

        if(Array.isArray(id)){
            qsArray.push({
                id
            });
        }

        if(options.include){
            let include = Array.isArray(options.include) ?  options.include : [options.include];
            for(let  i = 0 ;  i < include.length; i++){

                let _include = include[i];
                let model = typeof _include === "object" ? _include.model : _include;
                let attributes = typeof _include === "object" ? _include.attributes : undefined;

                qsArray.push({
                    ['include_' + model] : 'true'
                });

                if(attributes){
                    qsArray.push({
                        [`include_${model}_fields`]: attributes
                    });
                }

            }
        }

        if(options.pagination){

            let pag = options.pagination;

            if(pag.hasOwnProperty('offset') && pag.hasOwnProperty('limit')){
                qsArray.push({ 'limit': pag.limit });
                qsArray.push({ 'offset': pag.offset });
            }
            else if(pag.hasOwnProperty('perPage') && pag.hasOwnProperty('page')) {
                qsArray.push({ 'limit': pag.perPage });
                qsArray.push({ 'offset': (pag.page - 1) * pag.perPage });
            }

        }

        if(options.sort){
            qsArray.push({ 'order' : options.sort.join(",") });
        }

        if(options.search){

            qsArray.push({ 'filter_keyword' : options.search.keyword });
            qsArray.push({'filter_method' : options.search.method || 'inside' });

            if(options.search.fields)
                qsArray.push({'filter_fields' : options.search.fields.join(",") });
        }

        if(options.filter){

            let filter = undefined;

            /**
             * Applies filter
             * @param obj The filter object
             * @param custom Indicates the object is for custom filtering. Thus, has custom operator...
             */
            const applyFilter = (obj , custom) => {

                if(!filter){
                    filter = [];
                }

                if(custom){
                    filter.push({ field: obj.key , value: obj.value , operator: obj.operator || "=" , cast: obj.value })
                }
                else{

                    Object.keys(obj).forEach((k) => {
                        filter.push({ field : k , value: obj[k] , operator: '=' });
                    });

                }
            };

            if(Array.isArray(options.filter)){

                for(let i = 0 ; i < options.filter.length ; i++)
                    applyFilter(options.filter[i],true);
            }
            else if(typeof options.filter === "object"){
                applyFilter(options.filter);
            }
            else if(typeof options.filter === "string"){
                filter = options.filter;
            }

            if(filter){

                if(Array.isArray(filter))
                    qsArray.push({ '_filters': filter.map((f) => { return `${f.field}${f.operator}${encodeURIComponent(f.value)}${f.cast ? `:${f.cast}` : ''}`; }).join(",") });
                else
                    qsArray.push({ '_filters': filter });
            }
        }


        //  merge all queries to one big object
        let _mergeQuery = { };
        qsArray.forEach(q => {
            const k = Object.keys(q)[0];
            _mergeQuery[k] = q[k];
        });

        return Object.keys(_mergeQuery).length > 0 ? `${queryString}?${QueryString.stringify(_mergeQuery)}` : queryString;
    }

    __internalRunQuery(endpoint,options){

        const me = this;
        const isAuthenticated = this.isAuthenticated();

        if(!options.noAuth){
            if(!isAuthenticated){
                return new Error('Proxy not authenticated yet!');
            }
        }

        return new Promise((resolve,reject) => {

            let id;
            let method;

            if(options.delete){
                method = "delete";
                id = options.delete;
            }
            else if(options.update){
                method = "put";
                id = options.update;
            }
            else if(options.get){
                method = "get";
                id = options.get;
            }

            method = options.method || method || "get";

            if(Array.isArray(id) && method === "get"){
                endpoint += '/many';
            }

            let _url  = endpoint + (id ? ( Array.isArray(id)  ? '' : ("/" + id) ) : '' ) ;
            let reqChain = Request[method](this.getUrl(_url));

            reqChain.use(noCache);

            if(me.apiKey){
                reqChain.set('x-api-key',me.apiKey);
            }

            if(!options.noAuth) {
                let authInfo = me.getAuthInfo();
                if (authInfo) {
                    reqChain.set('Authorization','Bearer ' + authInfo.token);
                }
            }

            //  set headers
            let headers = options.headers;
            if(headers) {
                Object.keys(headers).forEach((k) => { if(headers[k]){ reqChain.set(k,headers[k]); } } );
            }

            //  set query parameters
            let query = options.query;
            if(query) {
                Object.keys(query).forEach((k) => {  reqChain.query({ [k]: query[k] })  });
            }

            if(Array.isArray(id)){
                reqChain.query({ id : id});
            }

            if(options.include){
                let include = Array.isArray(options.include) ?  options.include : [options.include];
                for(let  i = 0 ;  i < include.length; i++){

                    let _include = include[i];
                    let model = typeof _include === "object" ? _include.model : _include;
                    let attributes = typeof _include === "object" ? _include.attributes : undefined;

                    reqChain.query({
                        ['include_' + model] : 'true'
                    });

                    if(attributes){
                        reqChain.set(`x-include-${model}-fields`,attributes);
                    }

                }
            }

            if(isAuthenticated) {

                //
                const _notif = NotificationHandler.getInstance();
                if (_notif._connectionId) {
                    reqChain.set(_notif._header_name, _notif._connectionId);
                }

            }

            if(options.pagination){

                let pag = options.pagination;
                if(pag.hasOwnProperty('offset') && pag.hasOwnProperty('limit')){
                    reqChain.set('x-limit',pag.limit);
                    reqChain.set('x-offset',pag.offset);
                }
                else if(pag.hasOwnProperty('perPage') && pag.hasOwnProperty('page')) {
                    reqChain.set('x-limit', pag.perPage);
                    reqChain.set('x-offset', (pag.page - 1) * pag.perPage);
                }

                //  send actual count of items before applying filters and other properties
                reqChain.set('x-actual-count',true);
            }

            if(options.sort){
                reqChain.set('x-order',options.sort.join(","));
            }

            if(options.search){

                reqChain.query( { 'filter_keyword' : options.search.keyword });
                reqChain.query( {'filter_method' : options.search.method || 'inside' });

                if(options.search.fields)
                    reqChain.query({'filter_fields' : options.search.fields.join(",") });
            }

            if(options.exclude){
                reqChain.set('x-exclude-id' , (Array.isArray(options.exclude) ? options.exclude : [options.exclude]).join(","))
            }

            if(options.fields) {
                reqChain.set('x-fields' , typeof options.fields === "string" ? options.fields : options.fields.join(","));
            }

            if(options.filter){

                let filter = undefined;

                /**
                 * Applies filter
                 * @param obj The filter object
                 * @param custom Indicates the object is for custom filtering. Thus, has custom operator...
                 */
                const applyFilter = (obj , custom) => {

                    if(!filter){
                        filter = [];
                    }

                    if(custom){
                        filter.push({ field: obj.key , value: obj.value , operator: obj.operator || "=" , cast: obj.cast })
                    }
                    else{

                        Object.keys(obj).forEach((k) => {
                            filter.push({ field : k , value: obj[k] , operator: '=' });
                        });

                    }
                };

                if(Array.isArray(options.filter)){

                    for(let i = 0 ; i < options.filter.length ; i++)
                        applyFilter(options.filter[i],true);
                }
                else if(typeof options.filter === "object"){
                    applyFilter(options.filter);
                }
                else if(typeof options.filter === "string"){
                    filter = options.filter;
                }

                if(filter){

                    if(Array.isArray(filter))
                        reqChain.set('x-filter', filter.map((f) => { return `${f.field}${f.operator}${encodeURIComponent(f.value)}${f.cast ? `:${f.cast}` : ''}`; }).join(",") );
                    else
                        reqChain.set('x-filter',filter);
                }
            }

            if(options.verify){
                reqChain.set('x-verify', options.verify);
            }


            if(options.type){
                reqChain.type(options.type);
            }

            //  set content
            if(options.content){
                reqChain.send(options.content);
            }

            //  pipe stream to response
            if(options.pipe){
                options.pipe(reqChain);
            }
            else {

                //  send request
                reqChain.end(function (err, response) {

                    if (options.noAuth)
                        processResponse();
                    else {

                        //  inspect response before passing control to processResponse
                        me.inspectResponse(response, processResponse, (err) => {
                                PrepareError(err,err ? err.response: null);
                                reject(err);
                            },
                            () => {
                                return me.__internalRunQuery(endpoint,options).then(resolve);
                            });
                    }

                    function processResponse() {

                        if (err) {
                            PrepareError(err, response);
                            return reject(err);
                        }

                        //
                        if (response) {
                            PrepareResponse(response);
                        }

                        return resolve(response);
                    }
                });
            }

        });
    }

    /**
     * Executes the request
     * @param {String|Object} endpoint
     * @param {Object} options
     * @returns {Promise}
     */
    processRequest(endpoint,options){

        //  support for one arg call
        if(typeof endpoint == "object"){
            options = endpoint;
            endpoint = options.url;
        }

        if(!options.noAuth){
            return this.preExecuteRequest().then(() => this.__internalRunQuery(endpoint,options));
        }
        else {
            return this.__internalRunQuery(endpoint,options);
        }

    }
}


// WEBPACK FOOTER //
// ./src/common/TTLAsyncProxy.js